import React, { useState, useEffect } from "react";
import {
  Page,
  Layout,
  Card,
  Text,
  Button,
  Banner,
  Spinner,
  Box,
  BlockStack,
  InlineStack,
  Badge,
  TextField,
  Divider,
  ResourceList,
  ResourceItem,
} from "@shopify/polaris";
import { useShop } from "../context/ShopContext.jsx";
import { useI18n } from "../context/I18nContext.jsx";

const API_BASE = "/api";

export default function IntegrationPage() {
  const { connected, loading, connectionInfo, shops, shopDomain, switchShop, disconnect, checkConnection, apiFetch } = useShop();
  const { t } = useI18n();
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [disconnecting, setDisconnecting] = useState(null);
  const [shopLanguage, setShopLanguage] = useState("nl");
  const [showroomCopied, setShowroomCopied] = useState(false);

  const showroomUrl = shopDomain
    ? `${window.location.origin}/showroom?shop=${encodeURIComponent(shopDomain)}`
    : "";

  const copyShowroomUrl = () => {
    navigator.clipboard.writeText(showroomUrl).then(() => {
      setShowroomCopied(true);
      setTimeout(() => setShowroomCopied(false), 2000);
    }).catch(() => {});
  };

  // Fetch shop language for widget embed code
  useEffect(() => {
    if (connected && shopDomain) {
      apiFetch("/settings")
        .then((data) => setShopLanguage(data?.language || "nl"))
        .catch(() => setShopLanguage("nl"));
    }
  }, [connected, shopDomain]);

  const NOSCRIPT_TEXT = {
    nl: "Virtueel Passen vereist JavaScript",
    en: "Virtual Try-On requires JavaScript",
    de: "Virtuell Anprobieren erfordert JavaScript",
  };

  // Show success banner after OAuth redirect
  const justConnected =
    new URLSearchParams(window.location.search).get("connected") === "true";

  useEffect(() => {
    if (justConnected && connected) {
      setSuccess(t("integration.connectedSuccess"));
    }
  }, [justConnected, connected]);

  const handleDisconnect = async (domain) => {
    if (!confirm(t("integration.confirmDisconnect"))) return;
    setDisconnecting(domain);
    try {
      await fetch(`${API_BASE}/auth/disconnect`, {
        method: "DELETE",
        headers: { "x-shop-domain": domain },
      });
      await checkConnection();
      setSuccess(t("integration.disconnected"));
    } catch (err) {
      setError(err.message);
    } finally {
      setDisconnecting(null);
    }
  };

  const handleReconnect = (domain) => {
    window.location.href = `${API_BASE}/auth?shop=${encodeURIComponent(domain)}`;
  };

  return (
    <Page title={t("integration.title")}>
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          </Layout.Section>
        )}
        {success && (
          <Layout.Section>
            <Banner tone="success" onDismiss={() => setSuccess(null)}>
              {success}
            </Banner>
          </Layout.Section>
        )}

        {/* Connected shops list */}
        {shops.length > 0 && (
          <Layout.Section>
            <Card>
              <Box padding="400">
                <BlockStack gap="400">
                  <Text variant="headingMd" as="h2">
                    {t("integration.connectedShops")}
                  </Text>
                  <Text variant="bodyMd" tone="subdued">
                    {t("integration.connectedShopsDesc")}
                  </Text>
                  <Divider />
                  {shops.map((shop) => (
                    <Box key={shop.shopDomain} padding="200" borderRadius="200"
                      background={shop.shopDomain === shopDomain ? "bg-surface-selected" : undefined}>
                      <InlineStack align="space-between" blockAlign="center" gap="300">
                        <InlineStack gap="300" blockAlign="center">
                          <Badge tone={shop.shopDomain === shopDomain ? "success" : undefined}>
                            {shop.shopDomain === shopDomain ? t("integration.active") : t("integration.connected")}
                          </Badge>
                          <Text variant="bodyMd" fontWeight="semibold">
                            {shop.shopDomain}
                          </Text>
                          {shop.installedAt && (
                            <Text variant="bodySm" tone="subdued">
                              {new Date(shop.installedAt).toLocaleDateString()}
                            </Text>
                          )}
                        </InlineStack>
                        <InlineStack gap="200">
                          {shop.shopDomain !== shopDomain && (
                            <Button size="slim" onClick={() => switchShop(shop.shopDomain)}>
                              {t("integration.switchTo")}
                            </Button>
                          )}
                          <Button size="slim" onClick={() => handleReconnect(shop.shopDomain)}>
                            {t("integration.reconnect")}
                          </Button>
                          <Button size="slim" tone="critical"
                            loading={disconnecting === shop.shopDomain}
                            onClick={() => handleDisconnect(shop.shopDomain)}>
                            {t("integration.disconnect")}
                          </Button>
                        </InlineStack>
                      </InlineStack>
                    </Box>
                  ))}
                </BlockStack>
              </Box>
            </Card>
          </Layout.Section>
        )}

        {/* Add new shop */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <BlockStack gap="400">
                <Text variant="headingMd" as="h2">
                  {shops.length > 0 ? t("integration.addShop") : t("integration.shopifyConnection")}
                </Text>
                <Text variant="bodyMd" tone="subdued">
                  {t("integration.connectionDesc")}
                </Text>

                {loading ? (
                  <InlineStack align="center">
                    <Spinner size="small" />
                    <Text variant="bodyMd">{t("integration.checkingStatus")}</Text>
                  </InlineStack>
                ) : (
                  <BlockStack gap="400">
                    <Divider />
                    <Text variant="bodyMd">{t("integration.installViaAppStore")}</Text>
                    <Text variant="bodyMd" tone="subdued">{t("integration.installViaAppStoreHelp")}</Text>
                  </BlockStack>
                )}
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <Box padding="400">
              <BlockStack gap="300">
                <Text variant="headingMd" as="h2">
                  {t("integration.howItWorks")}
                </Text>
                <BlockStack gap="200">
                  <Text variant="bodyMd">
                    <strong>1.</strong> {t("integration.step1")}
                  </Text>
                  <Text variant="bodyMd">
                    <strong>2.</strong> {t("integration.step2")}
                  </Text>
                  <Text variant="bodyMd">
                    <strong>3.</strong> {t("integration.step3")}
                  </Text>
                  <Text variant="bodyMd">
                    <strong>4.</strong> {t("integration.step4")}
                  </Text>
                  <Text variant="bodyMd">
                    <strong>5.</strong> {t("integration.step5")}
                  </Text>
                </BlockStack>
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>

        {connected && (
          <Layout.Section>
            <Card>
              <Box padding="400">
                <BlockStack gap="400">
                  <Text variant="headingMd" as="h2">
                    {t("integration.widgetTitle")}
                  </Text>
                  <Text variant="bodyMd" tone="subdued">
                    {t("integration.widgetDesc")}
                  </Text>

                  <Divider />

                  <BlockStack gap="200">
                    <Text variant="headingSm" as="h3">
                      {t("integration.widgetOption1Title")}
                    </Text>
                    <BlockStack gap="100">
                      <Text variant="bodyMd">
                        <strong>1.</strong> {t("integration.widgetOption1Step1")}
                      </Text>
                      <Text variant="bodyMd">
                        <strong>2.</strong> {t("integration.widgetOption1Step2")}
                      </Text>
                      <Text variant="bodyMd">
                        <strong>3.</strong> {t("integration.widgetOption1Step3")}
                      </Text>
                      <Text variant="bodyMd">
                        <strong>4.</strong> {t("integration.widgetOption1Step4")}
                      </Text>
                      <Text variant="bodyMd">
                        <strong>5.</strong> {t("integration.widgetOption1Step5")}
                      </Text>
                    </BlockStack>
                    <div style={{
                      background: "#1e1e1e",
                      color: "#d4d4d4",
                      padding: "16px",
                      borderRadius: 8,
                      fontFamily: "'Fira Code', 'Consolas', monospace",
                      fontSize: 13,
                      lineHeight: 1.5,
                      overflowX: "auto",
                      whiteSpace: "pre",
                    }}>
{`<div data-zyncfit-container>
  <noscript>${NOSCRIPT_TEXT[shopLanguage] || NOSCRIPT_TEXT.nl}</noscript>
</div>
<script src="https://zync-fit.vercel.app/widget.js?v=3" defer></script>`}
                    </div>
                    <Text variant="bodySm" tone="subdued">
                      {t("integration.widgetOption1Hint")}
                    </Text>
                  </BlockStack>

                  <Divider />

                  <BlockStack gap="200">
                    <Text variant="headingSm" as="h3">
                      {t("integration.widgetOption2Title")}
                    </Text>
                    <Text variant="bodySm" tone="subdued">
                      {t("integration.widgetOption2Desc")}
                    </Text>
                    <div style={{
                      background: "#1e1e1e",
                      color: "#d4d4d4",
                      padding: "16px",
                      borderRadius: 8,
                      fontFamily: "'Fira Code', 'Consolas', monospace",
                      fontSize: 13,
                      lineHeight: 1.5,
                      overflowX: "auto",
                      whiteSpace: "pre",
                    }}>
{`<script src="https://zync-fit.vercel.app/widget.js?v=3" defer></script>`}
                    </div>
                    <Text variant="bodySm" tone="subdued">
                      {t("integration.widgetOption2Hint")}
                    </Text>
                  </BlockStack>
                </BlockStack>
              </Box>
            </Card>
          </Layout.Section>
        )}

        {/* Showroom menu link */}
        {connected && shopDomain && (
          <Layout.Section>
            <Card>
              <Box padding="400">
                <BlockStack gap="300">
                  <Text variant="headingMd" as="h2">
                    {t("integration.showroomTitle")}
                  </Text>
                  <Text variant="bodyMd" tone="subdued">
                    {t("integration.showroomDesc")}
                  </Text>
                  <InlineStack gap="200" blockAlign="center" wrap={false}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <TextField
                        label=""
                        labelHidden
                        value={showroomUrl}
                        readOnly
                        autoComplete="off"
                      />
                    </div>
                    <Button onClick={copyShowroomUrl}>
                      {showroomCopied ? t("integration.showroomCopied") : t("integration.showroomCopy")}
                    </Button>
                  </InlineStack>
                </BlockStack>
              </Box>
            </Card>
          </Layout.Section>
        )}
      </Layout>
    </Page>
  );
}
